import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const tipos = [
  {
    title: "Toldo enrollable horizontal",
    desc: "Sistema de lona que se extiende horizontalmente sobre tu pérgola existente. Protege del sol manteniendo ventilación. Fácil de motorizar.",
    tag: "Sobre pérgola existente",
  },
  {
    title: "Pérgola bioclimática con lamas orientables",
    desc: "Lamas de aluminio orientables que regulan luz, sombra y ventilación. Resistente al viento y lluvia. La máxima tecnología exterior.",
    tag: "Máxima tecnología",
  },
  {
    title: "Pérgola con techo de cristal + toldo",
    desc: "Estructura de cristal templado con toldo enrollable integrado. Disfruta de luz natural o sombra total a tu gusto.",
    tag: "Lujo y versatilidad",
  },
  {
    title: "Glass curtain (cerramiento vidriado)",
    desc: "Sistema de paneles de cristal deslizantes que cierran completamente tu pérgola. Usable todo el año, protege del viento y lluvia.",
    tag: "Uso 365 días",
  },
];

const ideal = [
  { emoji: "🍽️", title: "Restaurantes y chiringuitos", desc: "Crea terrazas climatizables en plena Costa del Sol. Aumenta capacidad y confort de clientes todo el año." },
  { emoji: "🏖️", title: "Áticos con terraza", desc: "Aprovecha al máximo tu ático en Málaga con una pérgola bioclimática. Vistas protegidas del sol intenso." },
  { emoji: "🌳", title: "Jardines premium villas", desc: "Villas en Marbella, Estepona o Benahavís. Pérgolas de diseño que integran piscina, jardín y terraza." },
  { emoji: "🏨", title: "Hoteles y resorts", desc: "Terrazas de bar, chill-out, zonas de desayuno. Instalaciones a medida con acabados de lujo y gran durabilidad." },
];

const materiales = [
  {
    icon: "ri-building-2-line",
    title: "Acero galvanizado o aluminio lacado",
    desc: "Estructuras anti-salitre para resistir el ambiente marino de la Costa del Sol. Garantía estructural de 10 años.",
  },
  {
    icon: "ri-sun-cloudy-line",
    title: "Lonas Tempotest, Sauleda",
    desc: "Lonas técnicas de alta gama anti-UV, anti-moho y auto-limpiables. Colores duraderos incluso bajo el sol de Málaga.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Vidrios templados de seguridad",
    desc: "Para cerramientos glass curtain: vidrio templado de 8-10mm con tratamiento solar. Seguridad y confort térmico.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Motores Somfy + sensores climáticos",
    desc: "Automatización completa: sensores de viento, lluvia, sol y temperatura. Control desde app TaHoma o voz (Alexa/Google).",
  },
];

const precios = [
  { tipo: "Pérgola simple manual", precio: "2.500 - 4.000 €", emoji: "🏗️" },
  { tipo: "Pérgola bioclimática", precio: "4.000 - 8.000 €", emoji: "⚡" },
  { tipo: "Pérgola + glass curtain", precio: "6.000 - 12.000 €", emoji: "🏠" },
  { tipo: "Veranda completa", precio: "Desde 10.000 €", emoji: "🏨" },
];

const faqs = [
  {
    q: "¿Necesito licencia de obra?",
    a: "Depende del tipo. Un toldo enrollable sobre pérgola existente no suele requerir obra mayor. Una pérgola bioclimática o veranda completa puede requerir declaración responsable en el Ayuntamiento de Málaga. Nosotros te asesoramos en todo el trámite y facilitamos los planos técnicos.",
  },
  {
    q: "¿Cuánto tarda la instalación?",
    a: "Un toldo enrollable sobre pérgola: 1 día. Una pérgola bioclimática estándar: 2-3 días. Una veranda completa con glass curtain: 3-5 días. El plazo de fabricación previa es de 3-4 semanas para estructuras a medida.",
  },
  {
    q: "¿Resiste lluvia y viento fuerte?",
    a: "Sí. Las pérgolas bioclimáticas resisten vientos de hasta 120 km/h cuando las lamas están cerradas. Las lonas Tempotest son impermeables. Las estructuras de aluminio o acero galvanizado están calculadas para la normativa de viento de la Costa del Sol. Los sensores recogen automáticamente ante ráfagas.",
  },
  {
    q: "¿Se puede climatizar el espacio?",
    a: "Totalmente. Con una pérgola bioclimática cerrada con glass curtains puedes instalar calefacción por infrarrojos, estufas de exterior o incluso aire acondicionado portátil. El espacio se convierte en una habitación exterior usable los 365 días del año.",
  },
  {
    q: "¿Garantía de los componentes?",
    a: "Estructura metálica: 10 años. Motor Somfy: 5 años. Lonas Tempotest/Sauleda: 5 años contra decoloración y desgarro. Vidrio templado: garantía de fábrica. Te entregamos todo por escrito.",
  },
];

export default function ToldosVeranda() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Toldos Veranda Málaga | Pérgolas Bioclimáticas Premium"
        description="Toldos para veranda y pérgola en Málaga. Pérgolas bioclimáticas, glass curtains, techos cristal. Premium."
        canonical="/toldos/toldos-veranda/"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name: "Toldos veranda" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> Premium para tu hogar
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Toldos para Veranda y Pérgola en Málaga
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  Crea un espacio exterior cubierto y elegante. Toldos para pérgolas bioclimáticas, glass curtains y verandas.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-2">
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> +10 años · desde 2015</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Motores Somfy</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Garantía 5 años</span>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                    alt="Pérgola bioclimática y veranda premium en Málaga"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TIPOS DE COBERTURA */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Tipos de cobertura para pérgolas</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {tipos.map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-white rounded-[20px] p-8 border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <span className="inline-block bg-primary/10 text-primary font-body font-semibold text-xs px-3 py-1 rounded-full mb-3">
                    {t.tag}
                  </span>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-3">{t.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* IDEAL PARA */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Ideal para</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ideal.map((item, i) => (
                <motion.div
                  key={item.title}
                  className="bg-surface-alt rounded-[20px] p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl mb-4">{item.emoji}</div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* MATERIALES PREMIUM */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Materiales premium</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto mt-4">
                En instalaciones premium solo utilizamos materiales de primera calidad, pensados para resistir el clima de la Costa del Sol.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {materiales.map((m, i) => (
                <motion.div
                  key={m.title}
                  className="bg-white rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${m.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{m.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{m.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios orientativos</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Incluyen estructura, materiales, instalación completa y garantía. Cada proyecto es único: pide presupuesto personalizado.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.tipo}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones, materiales y acabados. Presupuesto detallado tras visita técnica sin compromiso.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Transforma tu espacio exterior</h2>
            <p className="font-body text-lg text-white/90 max-w-xl mx-auto mb-8">
              Te visitamos gratis, medimos tu espacio y te presentamos un diseño 3D con presupuesto detallado. Sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="#presupuesto"
                className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-primary font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
              >
                Pedir presupuesto online
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre verandas y pérgolas</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-surface-alt rounded-2xl p-6"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}